import { useContext, useEffect, useMemo } from "react";
import { FeatureCollection } from "geojson";

import { HistoryContext } from "fond/history";
import { MapContext } from "fond/map/MapProvider";
import { MuPowerSolverLayerIds } from "fond/types";

import { PhaseNetwork } from "./phaseNetwork";

/**
 * Shows a live preview of the network's expected phasing while phase overrides are edited.
 */
export const useDrawPhasePreview = (network: PhaseNetwork): void => {
  const { drawControl } = useContext(MapContext);
  const history = useContext(HistoryContext);
  const drawState = history?.state as FeatureCollection | undefined;

  const overridePoints = useMemo(
    () => (drawState?.features ?? []).filter((feature) => feature.properties?.layerId === MuPowerSolverLayerIds.phaseOverrides),
    [drawState]
  );

  const previewPhases = useMemo(() => network.previewPhases(overridePoints), [network, overridePoints]);

  /** Solve-time phase of every radial wire, for restoring edges whose override is removed. */
  const solvePhases = useMemo(() => {
    const phases = new Map<string | number, unknown>();
    network.radialEdges.forEach((edge) => {
      if (edge.id != null) phases.set(edge.id, edge.properties?.Phase);
    });
    return phases;
  }, [network]);

  // Sync the predicted phases onto the wire draw copies.
  useEffect(() => {
    const draw = drawControl.current;
    if (!draw) return;

    let changed = false;
    const setPhase = (id: string | number, phase: unknown) => {
      if (typeof phase !== "string") return;
      const featureId = String(id);
      const feature = draw.get(featureId);
      if (feature && feature.properties?.Phase !== phase) {
        draw.setFeatureProperty(featureId, "Phase", phase);
        changed = true;
      }
    };

    solvePhases.forEach((phase, id) => {
      if (!previewPhases.has(id)) setPhase(id, phase);
    });
    previewPhases.forEach((phase, id) => setPhase(id, phase));

    // setFeatureProperty doesn't trigger a re-render, so we add an empty collection to force one.
    if (changed) draw.add({ type: "FeatureCollection", features: [] });
  }, [drawControl, drawState, previewPhases, solvePhases]);
};
